import { useState } from "react";
import { ChevronDown, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { btn } from "@/components/common";
import { EVENT_TYPES } from "@/lib/eventTypes";
import { addDays, fmtWeekday, itinerary } from "@/lib/trip";

const input = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
const key = () => Math.random().toString(36).slice(2);

function Events({ day, onChange }) {
  const items = day.items || [];
  const upd = (i, k) => (e) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));
  return (
    <div className="col-span-3 space-y-2.5 pl-[60px] sm:pl-[72px]">
      {items.map((it, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-slate-200 bg-slate-50/60 p-2.5" data-testid="itinerary-event-row">
          <div className="grid grid-cols-[1fr_1fr_36px] gap-2">
            <input type="time" className={input} value={it.time} onChange={upd(i, "time")} aria-label="Event time" />
            <div className="relative">
              <select className={`${input} appearance-none pr-8`} value={it.type || ""} onChange={upd(i, "type")} aria-label="Event type" data-testid="itinerary-event-type">
                <option value="">No icon</option>
                {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove event" className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
          <input className={input} placeholder="Event" value={it.text} onChange={upd(i, "text")} aria-label="Event description" />
          <input className={input} placeholder="Confirmation / booking # (optional)" value={it.conf || ""} onChange={upd(i, "conf")} aria-label="Confirmation or booking number" data-testid="itinerary-event-conf" />
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { time: "09:00", text: "", type: "", conf: "" }])} className="text-xs font-semibold text-navy-700 hover:underline" data-testid="itinerary-add-event-btn">+ Add event</button>
    </div>
  );
}

export function ItineraryEditor({ trip, onDone, withEvents }) {
  const { updateTrip } = useDemo();
  const [days, setDays] = useState(() => itinerary(trip).map(({ title, place, items }) => ({ key: key(), title, place, items })));
  const set = (k, field) => (e) => setDays(days.map((d) => (d.key === k ? { ...d, [field]: e.target.value } : d)));

  const save = () => {
    const clean = days.map(({ key: _k, ...d }) => { const title = d.title.trim() || "Day at leisure"; return { ...d, title, place: title, items: (d.items || []).filter((it) => (it.text || "").trim()).sort((a, b) => (a.time || "").localeCompare(b.time || "")) }; });
    updateTrip(trip.id, { days: clean, nights: clean.length - 1 });
    toast.success("Itinerary saved", { description: `${clean.length} days · ${clean.length - 1} nights` });
    onDone();
  };

  return (
    <div data-testid="itinerary-editor">
      {!withEvents && <p className="mb-4 flex items-start gap-2 rounded-xl bg-slate-50 px-3.5 py-2.5 text-xs text-slate-600"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />Set the overview for each day. Timed events within each day can be detailed once the client approves the quote.</p>}
      <ol className={withEvents ? "space-y-5" : "space-y-2"}>
        {days.map((d, i) => (
          <li key={d.key} className="grid grid-cols-[52px_1fr_36px] items-center gap-2 sm:grid-cols-[64px_1fr_36px]" data-testid={`itinerary-edit-day-${i + 1}`}>
            <div>
              <p className="text-xs font-semibold text-navy-900">Day {i + 1}</p>
              <p className="text-[10px] text-slate-400">{fmtWeekday(addDays(trip.departure, i)).replace(/^\w+, /, "")}</p>
            </div>
            <input className={input} placeholder="Port, location or day title" value={d.title} onChange={set(d.key, "title")} data-testid={`itinerary-day-title-${i + 1}`} />
            <button type="button" disabled={days.length <= 2} onClick={() => setDays(days.filter((x) => x.key !== d.key))} aria-label="Remove day" data-testid={`itinerary-day-remove-${i + 1}`} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent"><Trash2 className="h-4 w-4" /></button>
            {withEvents && <Events day={d} onChange={(items) => setDays(days.map((x) => (x.key === d.key ? { ...x, items } : x)))} />}
          </li>
        ))}
      </ol>
      <button type="button" onClick={() => setDays([...days, { key: key(), title: "Day at leisure", place: trip.destination, items: [] }])} data-testid="itinerary-add-day-btn" className={`${btn.ghost} mt-3`}><Plus className="h-4 w-4" />Add Day</button>
      <div className="mt-3 flex justify-end gap-2 border-t border-slate-200 pt-4">
        <button type="button" onClick={onDone} className={btn.secondary} data-testid="itinerary-cancel-btn">Cancel</button>
        <button type="button" onClick={save} className={btn.primary} data-testid="itinerary-save-btn">Save Itinerary</button>
      </div>
    </div>
  );
}
